/**
 * ExportsPanel — Export filtrado por toma + sitio (Excel maestro-matches).
 *
 * Endpoints:
 *  - GET /api/tomas → lista de tomas (objetos {id, cliente_id, fecha_fin, ...}).
 *    El "intake" para los endpoints de export se arma como `toma_<id>_<cliente_id>`.
 *  - GET /api/export-maestro-matches/count?intake=X&sitio=Y → { total }.
 *  - GET /api/export-maestro-matches?intake=X&sitio=Y → blob XLSX.
 *
 * El header X-User-Email se inyecta vía apiHelpers (instalado en AuthContext).
 *
 * Visibilidad: admin+ y user. Jeleni necesita acceso (puede ser rol 'user').
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { API_BASE } from '../config';
import './ExportsPanel.css';

const SITIOS = ['Tipti Ecuador', 'Frecuento', 'Tuti'];

function fmtDate(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('es-AR', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  } catch (_) { return '—'; }
}

function fmtSize(bytes) {
  if (!bytes || bytes < 1024) return `${bytes || 0} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function intakeFromToma(toma) {
  if (!toma || !toma.id || !toma.cliente_id) return null;
  return `toma_${toma.id}_${toma.cliente_id}`;
}

function tomaLabel(toma) {
  if (!toma) return '';
  const fecha = fmtDate(toma.fecha_fin || toma.fecha_inicio || toma.created_at);
  return `Toma ${toma.id} — ${fecha}`;
}

function ToastBanner({ toast, onClose }) {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [toast, onClose]);
  if (!toast) return null;
  return (
    <div className={`um-toast ${toast.type === 'error' ? 'um-toast-error' : ''}`}>
      {toast.message}
    </div>
  );
}

export default function ExportsPanel() {
  const [tomas, setTomas] = useState([]);
  const [loadingTomas, setLoadingTomas] = useState(true);
  const [tomasError, setTomasError] = useState(null);

  const [selectedTomaId, setSelectedTomaId] = useState('');
  const [selectedSitios, setSelectedSitios] = useState([]); // [] = todos

  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null); // { total, estSizeBytes }
  const [previewError, setPreviewError] = useState(null);

  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [toast, setToast] = useState(null);

  const [recentExports, setRecentExports] = useState(null); // null = no probado, [] = endpoint OK pero vacío
  const [recentExportsAvailable, setRecentExportsAvailable] = useState(true);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  // Cargar tomas
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingTomas(true);
      setTomasError(null);
      try {
        const res = await fetch(`${API_BASE}/api/tomas`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
        setTomas(list);
        if (list.length > 0) {
          setSelectedTomaId(String(list[0].id));
        }
      } catch (err) {
        if (!cancelled) setTomasError(err.message || 'Error al cargar tomas');
      } finally {
        if (!cancelled) setLoadingTomas(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Cargar historial exports (best-effort, no obliga)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/export-runs`);
        if (res.status === 404) {
          if (!cancelled) setRecentExportsAvailable(false);
          return;
        }
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
        setRecentExports(list);
      } catch (_) {
        if (!cancelled) setRecentExportsAvailable(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const selectedToma = useMemo(
    () => tomas.find(t => String(t.id) === String(selectedTomaId)) || null,
    [tomas, selectedTomaId]
  );

  const intake = useMemo(() => intakeFromToma(selectedToma), [selectedToma]);

  const handleSitioToggle = (sitio) => {
    setSelectedSitios(prev =>
      prev.includes(sitio) ? prev.filter(s => s !== sitio) : [...prev, sitio]
    );
    // Invalidar preview al cambiar filtros
    setPreviewData(null);
    setPreviewError(null);
  };

  const handleTomaChange = (e) => {
    setSelectedTomaId(e.target.value);
    setPreviewData(null);
    setPreviewError(null);
  };

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    if (intake) params.set('intake', intake);
    for (const s of selectedSitios) params.append('sitio', s);
    return params.toString();
  }, [intake, selectedSitios]);

  const handlePreview = async () => {
    if (!intake) {
      setPreviewError('Seleccioná una toma primero.');
      return;
    }
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewData(null);
    try {
      const qs = buildQuery();
      const res = await fetch(`${API_BASE}/api/export-maestro-matches/count?${qs}`);
      if (!res.ok) {
        const j = await res.json().catch(() => null);
        throw new Error(j?.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const total = Number(data?.total || 0);
      // Estimación: ~0.9KB por fila XLSX (regla práctica indicada por usuario)
      const estSizeBytes = Math.round(total * 0.9 * 1024);
      setPreviewData({ total, estSizeBytes });
    } catch (err) {
      setPreviewError(err.message || 'Error al obtener preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!intake) {
      showToast('Seleccioná una toma primero.', 'error');
      return;
    }
    setDownloading(true);
    setDownloadError(null);
    try {
      const qs = buildQuery();
      // Usamos fetch (no window.location) para que apiHelpers inyecte X-User-Email.
      const res = await fetch(`${API_BASE}/api/export-maestro-matches?${qs}`);
      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        throw new Error(errText || `HTTP ${res.status}`);
      }

      // Obtener filename del Content-Disposition si viene
      let filename = `match_${intake}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      const cd = res.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="?([^";]+)"?/i);
      if (m && m[1]) filename = m[1];

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);

      showToast(`Descarga iniciada: ${filename}`);
    } catch (err) {
      setDownloadError(err.message || 'Error al descargar');
      showToast(err.message || 'Error al descargar', 'error');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <div className="um-card">
        <div className="um-toolbar">
          <div className="um-toolbar-left">
            <strong style={{ fontSize: '0.9rem', color: '#111827' }}>
              Export Maestro-Matches por Toma
            </strong>
          </div>
        </div>

        <div className="exp-body">
          {tomasError && (
            <div className="um-error">Error al cargar tomas: {tomasError}</div>
          )}

          <div className="exp-grid">
            <div className="um-field">
              <label>Toma</label>
              <select
                value={selectedTomaId}
                onChange={handleTomaChange}
                disabled={loadingTomas || tomas.length === 0}
              >
                {loadingTomas && <option>Cargando…</option>}
                {!loadingTomas && tomas.length === 0 && <option>Sin tomas</option>}
                {tomas.map(t => (
                  <option key={t.id} value={t.id}>{tomaLabel(t)}</option>
                ))}
              </select>
              {intake && (
                <div className="exp-hint">
                  intake: <code>{intake}</code>
                </div>
              )}
            </div>

            <div className="um-field">
              <label>Sitios (vacío = todos)</label>
              <div className="exp-checkbox-group">
                {SITIOS.map(s => (
                  <label key={s} className="exp-checkbox-item">
                    <input
                      type="checkbox"
                      checked={selectedSitios.includes(s)}
                      onChange={() => handleSitioToggle(s)}
                    />
                    <span>{s}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="exp-actions">
            <button
              type="button"
              className="um-btn um-btn-ghost"
              onClick={handlePreview}
              disabled={!intake || previewLoading || downloading}
            >
              {previewLoading ? 'Calculando…' : 'Preview'}
            </button>
            <button
              type="button"
              className="um-btn um-btn-primary"
              onClick={handleDownload}
              disabled={!intake || downloading}
            >
              {downloading ? 'Generando XLSX…' : 'Descargar XLSX'}
            </button>
          </div>

          {previewError && <div className="um-error">{previewError}</div>}
          {downloadError && <div className="um-error">{downloadError}</div>}

          {previewData && (
            <div className="exp-preview-card">
              <div className="exp-preview-row">
                <span className="exp-preview-label">Matches</span>
                <span className="exp-preview-val">
                  {previewData.total.toLocaleString('es-AR')}
                </span>
              </div>
              <div className="exp-preview-row">
                <span className="exp-preview-label">Tamaño estimado XLSX</span>
                <span className="exp-preview-val">
                  ~{fmtSize(previewData.estSizeBytes)}
                </span>
              </div>
              {previewData.total === 0 && (
                <div className="exp-preview-warn">
                  No hay matches para los filtros seleccionados.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Exports recientes: solo si el endpoint existe */}
      {recentExportsAvailable && recentExports !== null && recentExports.length > 0 && (
        <div className="um-card exp-recent-card">
          <div className="um-toolbar">
            <strong style={{ fontSize: '0.9rem', color: '#111827' }}>
              Exports recientes
            </strong>
          </div>
          <div className="um-table-wrap">
            <table className="um-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Toma</th>
                  <th>Sitio(s)</th>
                  <th>Filas</th>
                  <th>Usuario</th>
                </tr>
              </thead>
              <tbody>
                {recentExports.slice(0, 20).map((r, i) => (
                  <tr key={r.id || i}>
                    <td>{fmtDate(r.created_at || r.timestamp)}</td>
                    <td>{r.intake || '—'}</td>
                    <td>{Array.isArray(r.sitios) ? r.sitios.join(', ') : (r.sitio || '—')}</td>
                    <td>{r.rows_exported ?? '—'}</td>
                    <td>{r.user_email || r.triggered_by || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!recentExportsAvailable && (
        <div className="exp-note">
          Historial de exports no disponible (endpoint <code>/api/admin/export-runs</code> no implementado).
        </div>
      )}

      <ToastBanner toast={toast} onClose={() => setToast(null)} />
    </>
  );
}
